import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiErrorText, apiRequest } from '../../utils';
import '../../pages/event/MemberHome.css';
import './ProfileExtras.css';

/* The rest of a person's public profile — what they would actually tell a colleague.

   ★ A card of its own, with a save of its own, sitting UNDER the profile form rather than inside
   it. That form is uncontrolled: it posts `Object.fromEntries(new FormData(...))` straight into
   `/api/edit-profile`, whose whitelist maps one-to-one onto `data.Partner` columns. Four of the
   fields here are lists and one is a set of picks; pushing those through a flat form post is how
   one of them ends up stored as the string "[object Object]".

   ★ The vocabularies come from the SERVER, not from a constant here. The store drops any token it
   does not know, so a screen that invented its own would offer a choice that silently never saved.

   ★ **And so do the SUGGESTIONS — what other people already wrote in the same field.** Free text
   is what makes these answers honest and it is also what makes them useless as chips: 'Padel',
   'padel tennis' and 'padel' are three filters for one hobby, and the community wall's chip rows
   are built out of exactly these values. Tapping a word somebody else used is one keystroke and
   it is how a platform ends up with one spelling; typing a new one is still right there beside it.

   ★ Every field is optional and nothing here is asked twice. "About you" is the profile's existing
   bio, edited a few centimetres above this card — a second box asking the same question would
   split what people write in half, and only one half is on the badge.

   ★ The last thing on the card is not a field but a SETTING: whether colleagues may put this
   person's name on a photograph. It rides the same blob and the same save (the store keeps it
   whatever else a save says), and it is here rather than on a settings screen of its own because
   this card is already the answer to "what do people see of me" — and because a privacy switch
   nobody finds is a privacy switch nobody has. */

/* Free text, in the order the editor draws them. The number is the maxLength, matching the cap
   the store applies — a field that lets you type 300 characters and stores 120 is a field that
   silently eats a sentence. */
const TEXTS = [['based_in', 80], ['superpower', 120], ['fun_fact', 200], ['motto', 200]];

/* ★ What you do here and who you sit with — first on the card, because it is the first thing a
   colleague looking for you needs, and because it is the pair the CRM cannot be trusted to hold:
   `data.Partner.JobTitle` / `Department` are refreshed by every prospect import, so a person's own
   answer lives in this blob and merely FALLS BACK to those columns. Same input as TEXTS, drawn
   above the lists rather than below them. */
const JOB = [['role', 80], ['department', 80]];

/* The three free lists. Same chip editor, three labels — and `open_to` is a fourth, drawn further
   down because it is the one that says what somebody will DO rather than who they are. */
const CHIPS = ['hobbies', 'languages', 'ask_me_about'];

const SINCE_YEAR_MIN = 1900;

/* The store's own cap. Kept here as well so the editor stops offering an "+ Add" and a strip of
   suggestions that would be dropped on the way in. */
const MAX_CHIPS = 12;

/* A suggestion strip past this is a wall of words above a field that needed one of them. The
   rest is one tap away. */
const SUGGEST_SHOWN = 10;

/* What other people wrote in this field: chips you tap instead of typing.

   ★ `onMouseDown` swallows the default, so the input above never loses focus and its `onBlur`
   never fires — otherwise tapping a suggestion while a half-typed word sits in the box would
   commit that half-typed word first and add TWO chips, one of them nonsense.

   Drawn only when it has something to offer: a strip that is empty, or whose every word is
   already picked, is a label above nothing. */
const Suggestions = ({ words, onPick, disabled }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  if (!words.length) return null;
  const shown = all ? words : words.slice(0, SUGGEST_SHOWN);
  const rest = words.length - shown.length;
  return (
    <div className="pfx-suggest">
      <span className="pfx-suggest__label">{t('pfSuggest')}</span>
      {shown.map((word) => (
        <button
          key={word.value}
          type="button"
          className={`bravos-tag bravos-tag--filter ${word.on ? 'bravos-tag--on' : ''}`}
          disabled={disabled}
          aria-pressed={!!word.on}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(word.value)}
        >
          {word.label}
        </button>
      ))}
      {(rest > 0 || all) && (
        <button type="button" className="bravos-tag__add" onMouseDown={(e) => e.preventDefault()}
                onClick={() => setAll(!all)}>
          {all ? t('pfSuggestLess') : t('pfSuggestMore', { n: rest })}
        </button>
      )}
    </div>
  );
};

/* One free-text chip row: the chips, an × on each, "+ Add" turning into an input, and under it
   what everybody else said. The classes are the roster's tag editor (MemberHome.css) — the same
   job, so the same chip.

   `renderLabel` exists for one field: `open_to` keeps translated labels for the five words the
   platform suggests everywhere, while a word somebody typed themselves renders as they typed it. */
const ChipRow = ({ label, hint, values, onChange, disabled, suggestions, renderLabel }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const list = values || [];
  const show = (word) => (renderLabel ? renderLabel(word) : word);

  /* The store de-duplicates and lower-cases too; doing it here as well is what stops the chip
     appearing twice for the second it takes the save to come back. */
  const add = (word) => {
    const clean = String(word || '').trim().toLowerCase();
    if (clean && !list.includes(clean) && list.length < MAX_CHIPS) onChange([...list, clean]);
  };

  const commit = () => {
    add(draft);
    setDraft('');
    setAdding(false);
  };

  /* While somebody is typing, the strip narrows to what they are typing — one box that is both a
     search through the words already in use and a way of writing a new one. */
  const typed = draft.trim().toLowerCase();
  const offered = (suggestions || [])
    .filter((word) => !list.includes(word))
    .filter((word) => !typed || word.includes(typed))
    .map((word) => ({ value: word, label: show(word) }));

  return (
    <div className="pfx-field">
      <span className="bravos-form__label">{label}</span>
      {hint && <p className="ce-note pfx-hint">{hint}</p>}
      <div className="bravos-tags">
        {list.map((word) => (
          <span key={word} className="bravos-tag">
            {show(word)}
            <button type="button" className="bravos-tag__x" disabled={disabled}
                    onClick={() => onChange(list.filter((x) => x !== word))}>
              ×
            </button>
          </span>
        ))}
        {adding ? (
          <input
            className="bravos-taginput"
            autoFocus
            value={draft}
            maxLength={40}
            placeholder={t('pfAddPlaceholder')}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setDraft(''); setAdding(false); }
            }}
          />
        ) : list.length < MAX_CHIPS && (
          <button type="button" className="bravos-tag__add" disabled={disabled}
                  onClick={() => setAdding(true)}>
            + {t('pfAdd')}
          </button>
        )}
      </div>
      {list.length < MAX_CHIPS && (
        <Suggestions words={offered} disabled={disabled} onPick={add} />
      )}
    </div>
  );
};

/* One free-text field, with the same strip under it wherever the answer is one other people also
   give — an office, a team. Tapping the word you already have takes it back off, so a chip is
   never a one-way door.

   Everything else here (a superpower, a motto) is nobody else's sentence, and its strip is simply
   never passed one. */
const TextRow = ({ fieldKey, max, value, onChange, disabled, suggestions }) => {
  const { t } = useTranslation();
  const current = value || '';
  const typed = current.trim().toLowerCase();
  const offered = (suggestions || [])
    .filter((word) => !typed || word.toLowerCase().includes(typed))
    .map((word) => ({ value: word, label: word, on: word.toLowerCase() === typed }));

  return (
    <div className="pfx-field">
      <label className="bravos-form__label" htmlFor={`pfx-${fieldKey}`}>{t(`pf_${fieldKey}`)}</label>
      <input id={`pfx-${fieldKey}`} type="text" className="gl-search" maxLength={max}
             value={current} placeholder={t(`pfHint_${fieldKey}`)}
             onChange={(e) => onChange(e.target.value)} />
      <Suggestions
        words={offered}
        disabled={disabled}
        onPick={(word) => onChange(word.toLowerCase() === typed ? '' : word)}
      />
    </div>
  );
};

const ProfileExtras = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const adopt = useCallback((res) => {
    setData(res);
    setDraft(res.profile || {});
  }, []);

  const load = useCallback(() => {
    apiRequest('GET', '/profile/extras')
      .then(adopt)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [adopt, t]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('PUT', '/profile/extras', { profile: draft })
      .then((res) => { adopt(res); setNotice(t('pfSaved')); })
      .catch((err) => setError(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return error ? <p className="bravos-cal__notice">{error}</p>
      : <span className="auth__spinner" />;
  }

  const stored = data.profile || {};
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const picks = draft.this_or_that || {};
  const thisYear = new Date().getFullYear();
  /* Absent means yes — the store writes the key only when somebody has switched it off, so an
     untouched profile and an explicit "yes" are the same blob and read the same here. */
  const taggable = draft.taggable !== false;
  /* ★ The opposite default from the one above it, and that is the point rather than a slip: being
     named in a photograph of a room you were in is opt-OUT, being listed in a directory of people
     you have never met is opt-IN. `!== false` there, `=== true` here. */
  const communityVisible = draft.community_visible === true;
  const tagCount = data.tag_count || 0;
  const suggest = data.suggestions || {};
  /* The five the platform suggests everywhere keep their translated label; anything somebody
     typed themselves reads as they typed it. */
  const openLabel = (word) => t(`pfOpen_${word}`, { defaultValue: word });

  /* Clicking the answer you already gave takes it back — the alternative is a pair you can never
     un-answer once you have tapped one of them by accident. */
  const pick = (key, value) => set('this_or_that',
    { ...picks, [key]: picks[key] === value ? undefined : value });

  return (
    /* ★ Enter never leaves this card. It is rendered inside the profile screen's own <form>, and
       an Enter in any text field here would submit THAT form — saving the name and navigating
       away with everything typed below still unsaved. Swallowed at the top rather than per input,
       so a field added later inherits it. */
    <div
      className="pfx"
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
    >
      <p className="bravos-q__hint">{t('pfNote')}</p>

      {JOB.map(([key, max]) => (
        <TextRow key={key} fieldKey={key} max={max} value={draft[key]} disabled={busy}
                 suggestions={suggest[key]} onChange={(value) => set(key, value)} />
      ))}

      {CHIPS.map((key) => (
        <ChipRow
          key={key}
          label={t(`pf_${key}`)}
          hint={t(`pfHint_${key}`)}
          values={draft[key]}
          suggestions={suggest[key]}
          onChange={(next) => set(key, next)}
          disabled={busy}
        />
      ))}

      {TEXTS.map(([key, max]) => (
        <TextRow key={key} fieldKey={key} max={max} value={draft[key]} disabled={busy}
                 suggestions={suggest[key]} onChange={(value) => set(key, value)} />
      ))}

      <div className="pfx-field">
        <label className="bravos-form__label" htmlFor="pfx-since">{t('pf_since_year')}</label>
        <input id="pfx-since" type="number" className="gl-search pfx-year"
               min={SINCE_YEAR_MIN} max={thisYear}
               value={draft.since_year || ''}
               onChange={(e) => set('since_year', e.target.value)} />
      </div>

      {/* Two taps each, and they are what gets a wall of blank tiles off zero. */}
      <div className="pfx-field">
        <span className="bravos-form__label">{t('pf_this_or_that')}</span>
        {Object.entries(data.this_or_that || {}).map(([key, options]) => (
          <div className="pfx-pair" key={key}>
            <span className="pfx-pair__label">{t(`pfTot_${key}`)}</span>
            {options.map((value) => (
              <button
                key={value}
                type="button"
                className={`bravos-tag bravos-tag--filter ${picks[key] === value ? 'bravos-tag--on' : ''}`}
                disabled={busy}
                aria-pressed={picks[key] === value}
                onClick={() => pick(key, value)}
              >
                {t(`pfTotv_${value}`)}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* The only field here that produces an action rather than a fact: everything before it
          says who somebody is, this says what they will do.

          ★ Free chips since 2026-09-08, where it used to be five tick-boxes. The five are still
          what the strip offers first and they still read in three languages — what changed is
          that "reviewing a CV" is now an answer somebody can give. `data.open_to` is the
          fallback: the vocabulary is on the payload whether or not anybody has said anything. */}
      <ChipRow
        label={t('pf_open_to')}
        hint={t('pfHint_open_to')}
        values={draft.open_to}
        suggestions={suggest.open_to || data.open_to}
        renderLabel={openLabel}
        onChange={(next) => set('open_to', next)}
        disabled={busy}
      />

      {/* ★ Last, and not a field at all — a SETTING. Everything above is something a person says
          about themselves and renders on their profile; this is something they say about what
          other people may do, it is read on a write path and it renders nowhere. It closes the
          card because that is the honest place for it: the answer to "and who gets to use all
          this?".

          The count is the point of the sentence. A switch that silently deleted somebody's name
          off eleven photographs would be a switch nobody presses twice — so it says the number
          first, and only the save carries it out. */}
      <div className="pfx-field pfx-field--setting">
        <span className="bravos-form__label">{t('pf_taggable')}</span>
        <label className="pfx-switch" htmlFor="pfx-taggable">
          <input
            id="pfx-taggable"
            type="checkbox"
            checked={taggable}
            disabled={busy}
            onChange={() => set('taggable', !taggable)}
          />
          <span>{t('pfTaggableLabel')}</span>
        </label>
        <p className="ce-note pfx-hint">{t('pfHint_taggable')}</p>
        {!taggable && tagCount > 0 && (
          <p className="bravos-cal__notice">{t('pfTaggableWillClear', { count: tagCount })}</p>
        )}
      </div>

      {/* The second setting, and the same kind of thing: not a field anybody reads off your
          profile but a permission other people's screens obey. It is also asked on the wall
          itself, where you can see what you would be joining — this is the copy of it that lives
          where somebody goes looking for their own settings rather than stumbling on it. */}
      <div className="pfx-field pfx-field--setting">
        <span className="bravos-form__label">{t('pf_community_visible')}</span>
        <label className="pfx-switch" htmlFor="pfx-community-visible">
          <input
            id="pfx-community-visible"
            type="checkbox"
            checked={communityVisible}
            disabled={busy}
            onChange={() => set('community_visible', !communityVisible)}
          />
          <span>{t('pfCommunityVisibleLabel')}</span>
        </label>
        <p className="ce-note pfx-hint">{t('pfHint_community_visible')}</p>
      </div>

      <div className="bravos-form__row mt-2">
        <button type="button" className="bravos-submit" disabled={busy || !dirty} onClick={save}>
          {t('save')}
        </button>
        {dirty && (
          <button type="button" className="bravos-submit bravos-submit--ghost" disabled={busy}
                  onClick={() => setDraft(stored)}>
            {t('cancel')}
          </button>
        )}
      </div>

      {notice && <p className="bravos-cal__notice"><Icon name="check" /> {notice}</p>}
      {error && <p className="bravos-cal__notice">{error}</p>}
    </div>
  );
};

export default ProfileExtras;
